import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { ClipboardList, Plus, Search, Trash2, ArrowDownCircle, ArrowUpCircle, Package, Calendar, Truck, Box } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StockLedger = () => {
  const { user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalEntries, setTotalEntries] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");

  // New entry dialog
  const [createDialog, setCreateDialog] = useState(false);
  const [entryType, setEntryType] = useState("in");
  const [poReference, setPoReference] = useState("");
  const [entryDate, setEntryDate] = useState(new Date().toISOString().split('T')[0]);
  const [transporterName, setTransporterName] = useState("");
  const [totalBoxes, setTotalBoxes] = useState(0);
  const [packagingTypes, setPackagingTypes] = useState("");
  const [entryNotes, setEntryNotes] = useState("");
  const [entryItems, setEntryItems] = useState([]);

  // Item search
  const [itemSearch, setItemSearch] = useState("");
  const [itemResults, setItemResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);

  // Submission guard
  const [submitting, setSubmitting] = useState(false);
  // Batch entry counter
  const [batchCount, setBatchCount] = useState(0);
  const [batchTotalQty, setBatchTotalQty] = useState(0);

  // View entry
  const [viewDialog, setViewDialog] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);

  const isAdmin = user?.role === "admin";

  useEffect(() => { fetchEntries(); }, [currentPage, dateFrom, dateTo, typeFilter]);

  const fetchEntries = async () => {
    try {
      const params = new URLSearchParams({ page: currentPage, page_size: 50 });
      if (dateFrom) params.append("date_from", dateFrom);
      if (dateTo) params.append("date_to", dateTo);
      if (typeFilter && typeFilter !== "all") params.append("entry_type", typeFilter);
      const r = await axios.get(`${API_URL}/stock-ledger?${params}`);
      setEntries(r.data.entries); setTotalEntries(r.data.total); setTotalPages(r.data.total_pages);
    } catch (err) { console.error("Ledger fetch failed:", err.message); toast.error("Failed to fetch ledger"); }
    finally { setLoading(false); }
  };

  // Debounced item search
  const searchItems = useCallback(async (query) => {
    if (!query || query.length < 2) { setItemResults([]); return; }
    setSearchLoading(true);
    try {
      const r = await axios.get(`${API_URL}/inventory/dropdown-search?q=${encodeURIComponent(query)}`);
      setItemResults(r.data);
    } catch (err) { console.error("Item search failed:", err.message); }
    finally { setSearchLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const t = setTimeout(() => searchItems(itemSearch), 250);
    return () => clearTimeout(t);
  }, [itemSearch, searchItems]);

  const addItemToEntry = (item) => {
    if (entryItems.find(i => i.item_id === item.id)) { toast.info("Already added"); return; }
    setEntryItems([...entryItems, { item_id: item.id, part_number: item.part_number, name: item.name, brand: item.brand || "", quantity: 1 }]);
    setItemSearch("");
    setItemResults([]);
  };

  const updateEntryItem = (index, field, value) => {
    const u = [...entryItems]; u[index][field] = field === "quantity" ? parseInt(value) || 0 : value; setEntryItems(u);
  };

  const removeEntryItem = (index) => setEntryItems(entryItems.filter((_, i) => i !== index));

  const openCreateDialog = (type) => {
    setEntryType(type); setPoReference(""); setEntryDate(new Date().toISOString().split('T')[0]);
    setTransporterName(""); setTotalBoxes(0); setPackagingTypes(""); setEntryNotes("");
    setEntryItems([]); setItemSearch(""); setItemResults([]);
    setBatchCount(0); setBatchTotalQty(0);
    setCreateDialog(true);
  };

  const handleSubmitEntry = async (keepOpen = false) => {
    if (submitting) return;
    if (entryItems.length === 0) { toast.error("Add at least one item"); return; }
    if (entryItems.some(i => i.quantity <= 0)) { toast.error("All quantities must be > 0"); return; }
    setSubmitting(true);
    try {
      const qty = entryItems.reduce((s, i) => s + i.quantity, 0);
      await axios.post(`${API_URL}/stock-ledger`, {
        entry_type: entryType, po_reference: poReference || null, date: entryDate,
        transporter_name: transporterName || null, total_boxes: totalBoxes || null,
        packaging_types: packagingTypes ? packagingTypes.split(",").map(s => s.trim()).filter(Boolean) : [],
        items: entryItems, notes: entryNotes || null,
      });
      if (keepOpen) {
        setBatchCount(prev => prev + 1);
        setBatchTotalQty(prev => prev + qty);
        setEntryItems([]); setItemSearch(""); setItemResults([]);
        toast.success(`Saved ${entryItems.length} items (${qty} qty). Add more items under same PO.`);
      } else {
        toast.success(`Stock ${entryType === "in" ? "IN" : "OUT"} recorded — inventory updated`);
        setCreateDialog(false);
      }
      fetchEntries();
    } catch (error) { toast.error(error.response?.data?.detail || "Failed to save entry"); }
    finally { setSubmitting(false); }
  };

  const handleDeleteEntry = async (id) => {
    if (!window.confirm("Delete this ledger entry? (Inventory changes will NOT be reversed)")) return;
    try { await axios.delete(`${API_URL}/stock-ledger/${id}`); toast.success("Entry deleted"); fetchEntries(); }
    catch { toast.error("Failed to delete"); }
  };

  if (loading) return <div className="p-8 text-slate-500">Loading stock ledger...</div>;

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="stock-ledger-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-4xl font-black text-slate-900 uppercase page-title" style={{ letterSpacing: "-0.02em" }}>
            Daily Stock Ledger
          </h1>
          <p className="text-slate-500 mt-1 text-sm">{totalEntries} entries recorded</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => openCreateDialog("in")} className="bg-green-600 hover:bg-green-700 text-white rounded-sm" data-testid="stock-in-btn">
            <ArrowDownCircle size={18} className="mr-2" /> Stock IN
          </Button>
          <Button onClick={() => openCreateDialog("out")} className="bg-red-600 hover:bg-red-700 text-white rounded-sm" data-testid="stock-out-btn">
            <ArrowUpCircle size={18} className="mr-2" /> Stock OUT
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-sm p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <Label className="text-xs text-slate-600 font-bold uppercase">Type</Label>
            <Select value={typeFilter} onValueChange={(v) => { setTypeFilter(v); setCurrentPage(1); }}>
              <SelectTrigger className="mt-1 rounded-sm border-slate-300"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white"><SelectItem value="all">All</SelectItem><SelectItem value="in">Stock IN</SelectItem><SelectItem value="out">Stock OUT</SelectItem></SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs text-slate-600 font-bold uppercase">From Date</Label><Input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setCurrentPage(1); }} className="mt-1 rounded-sm border-slate-300" /></div>
          <div><Label className="text-xs text-slate-600 font-bold uppercase">To Date</Label><Input type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setCurrentPage(1); }} className="mt-1 rounded-sm border-slate-300" /></div>
          <div className="flex items-end"><Button variant="outline" onClick={() => { setDateFrom(""); setDateTo(""); setTypeFilter("all"); setCurrentPage(1); }} className="w-full">Clear</Button></div>
        </div>
      </div>

      {/* Entries Table */}
      <div className="bg-white border border-slate-200 rounded-sm">
        {entries.length === 0 ? (
          <div className="p-12 text-center"><ClipboardList className="mx-auto text-slate-300 mb-4" size={64} /><p className="text-slate-500">No ledger entries found</p><p className="text-xs text-slate-400 mt-1">Click Stock IN or Stock OUT to record inventory movement</p></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="ledger-table">
              <thead className="bg-slate-100">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-bold text-slate-600 uppercase">Date</th>
                  <th className="text-center px-4 py-3 text-xs font-bold text-slate-600 uppercase">Type</th>
                  <th className="text-left px-4 py-3 text-xs font-bold text-slate-600 uppercase">PO Ref</th>
                  <th className="text-left px-4 py-3 text-xs font-bold text-slate-600 uppercase">Transporter</th>
                  <th className="text-center px-4 py-3 text-xs font-bold text-slate-600 uppercase">Items</th>
                  <th className="text-center px-4 py-3 text-xs font-bold text-slate-600 uppercase">Total Qty</th>
                  <th className="text-left px-4 py-3 text-xs font-bold text-slate-600 uppercase">By</th>
                  <th className="text-center px-4 py-3 text-xs font-bold text-slate-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {entries.map((entry) => (
                  <tr key={entry.id} className="hover:bg-slate-50 transition-colors" data-testid={`ledger-row-${entry.id}`}>
                    <td className="px-4 py-4 font-medium text-slate-900">{new Date(entry.date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</td>
                    <td className="px-4 py-4 text-center">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-bold uppercase rounded-sm ${entry.entry_type === "in" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>
                        {entry.entry_type === "in" ? <ArrowDownCircle size={12} /> : <ArrowUpCircle size={12} />}
                        {entry.entry_type}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-slate-700">{entry.po_reference || "-"}</td>
                    <td className="px-4 py-4 text-slate-700">{entry.transporter_name || "-"}</td>
                    <td className="px-4 py-4 text-center font-bold text-slate-900">{entry.items?.length || 0}</td>
                    <td className="px-4 py-4 text-center font-bold text-slate-900">{entry.total_quantity || 0}</td>
                    <td className="px-4 py-4 text-slate-600 text-xs">{entry.created_by_name}</td>
                    <td className="px-4 py-4">
                      <div className="flex items-center justify-center gap-1">
                        <Button size="sm" variant="ghost" onClick={() => { setSelectedEntry(entry); setViewDialog(true); }} className="text-slate-600 hover:text-slate-900" title="View">
                          <ClipboardList size={16} />
                        </Button>
                        {isAdmin && <Button size="sm" variant="ghost" onClick={() => handleDeleteEntry(entry.id)} className="text-red-600 hover:text-red-700 hover:bg-red-50" title="Delete"><Trash2 size={16} /></Button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mt-4 bg-white border border-slate-200 rounded-sm p-3 gap-2">
          <p className="text-sm text-slate-500">Page {currentPage} of {totalPages} ({totalEntries} entries)</p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={currentPage <= 1} onClick={() => setCurrentPage(currentPage - 1)}>Previous</Button>
            <Button variant="outline" size="sm" disabled={currentPage >= totalPages} onClick={() => setCurrentPage(currentPage + 1)}>Next</Button>
          </div>
        </div>
      )}

      {/* Create Entry Dialog */}
      <Dialog open={createDialog} onOpenChange={setCreateDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold uppercase flex items-center gap-2">
              {entryType === "in" ? <ArrowDownCircle size={22} className="text-green-600" /> : <ArrowUpCircle size={22} className="text-red-600" />}
              Stock {entryType === "in" ? "IN" : "OUT"} Entry
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-5 py-2">
            {/* PO Details — entered once */}
            <div className="p-4 bg-slate-50 border border-slate-200 rounded-sm space-y-4">
              <h3 className="text-xs font-bold uppercase text-slate-500 tracking-wider flex items-center gap-2"><Calendar size={14} /> Entry Details (enter once per PO)</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div><Label className="text-xs font-bold uppercase text-slate-600">PO Reference Number</Label><Input value={poReference} onChange={(e) => setPoReference(e.target.value)} placeholder="PO-XXXXXX" className="mt-1 rounded-sm" data-testid="ledger-po-ref" /></div>
                <div><Label className="text-xs font-bold uppercase text-slate-600">Date</Label><Input type="date" value={entryDate} onChange={(e) => setEntryDate(e.target.value)} className="mt-1 rounded-sm" data-testid="ledger-date" /></div>
                <div><Label className="text-xs font-bold uppercase text-slate-600">Transporter Name</Label><Input value={transporterName} onChange={(e) => setTransporterName(e.target.value)} placeholder="Transporter" className="mt-1 rounded-sm" data-testid="ledger-transporter" /></div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div><Label className="text-xs font-bold uppercase text-slate-600">Total Boxes</Label><Input type="number" value={totalBoxes} onChange={(e) => setTotalBoxes(parseInt(e.target.value) || 0)} className="mt-1 rounded-sm" data-testid="ledger-boxes" /></div>
                <div><Label className="text-xs font-bold uppercase text-slate-600">Packaging Types (comma separated)</Label><Input value={packagingTypes} onChange={(e) => setPackagingTypes(e.target.value)} placeholder="Box, Pallet, Crate" className="mt-1 rounded-sm" data-testid="ledger-packaging" /></div>
                <div><Label className="text-xs font-bold uppercase text-slate-600">Notes</Label><Input value={entryNotes} onChange={(e) => setEntryNotes(e.target.value)} placeholder="Optional notes" className="mt-1 rounded-sm" /></div>
              </div>
            </div>

            {/* Smart Item Search */}
            <div>
              <h3 className="text-xs font-bold uppercase text-slate-500 tracking-wider flex items-center gap-2 mb-2"><Package size={14} /> Add Items</h3>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                <Input value={itemSearch} onChange={(e) => setItemSearch(e.target.value)} placeholder="Type to search inventory (min 2 chars)..." className="pl-9 rounded-sm border-slate-300" data-testid="ledger-item-search" />
              </div>
              {itemResults.length > 0 && (
                <div className="border border-slate-200 rounded-sm mt-1 max-h-40 overflow-y-auto bg-white shadow-lg">
                  {itemResults.map((item) => (
                    <button key={item.id} onClick={() => addItemToEntry(item)} className="w-full text-left px-3 py-2 hover:bg-orange-50 border-b border-slate-100 last:border-0 flex items-center justify-between text-sm" data-testid={`dropdown-item-${item.part_number}`}>
                      <div>
                        <span className="font-medium text-slate-900">{item.part_number}</span>
                        <span className="text-slate-500 ml-2">{item.name}</span>
                        {item.brand && <span className="text-slate-400 ml-1">({item.brand})</span>}
                      </div>
                      <span className={`text-xs font-bold ${item.quantity === 0 ? "text-red-600" : item.quantity <= 10 ? "text-orange-500" : "text-slate-500"}`}>{item.quantity} in stock</span>
                    </button>
                  ))}
                </div>
              )}
              {searchLoading && <p className="text-xs text-slate-400 mt-1">Searching...</p>}
            </div>

            {/* Items List */}
            {entryItems.length > 0 && (
              <div>
                <h3 className="text-xs font-bold uppercase text-slate-500 tracking-wider mb-2">{entryItems.length} Items Added</h3>
                <div className="border rounded-sm overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-100"><tr><th className="text-left px-3 py-2 text-xs font-bold uppercase">Part #</th><th className="text-left px-3 py-2 text-xs font-bold uppercase">Name</th><th className="text-left px-3 py-2 text-xs font-bold uppercase">Brand</th><th className="text-center px-3 py-2 text-xs font-bold uppercase">Qty</th><th className="px-2 py-2"></th></tr></thead>
                    <tbody className="divide-y">
                      {entryItems.map((item, i) => (
                        <tr key={item.part_number || `entry-item-${i}`}>
                          <td className="px-3 py-2 font-medium text-slate-900">{item.part_number}</td>
                          <td className="px-3 py-2 text-slate-700">{item.name}</td>
                          <td className="px-3 py-2 text-slate-500">{item.brand || "-"}</td>
                          <td className="px-3 py-2"><Input type="number" min="1" value={item.quantity} onChange={(e) => updateEntryItem(i, "quantity", e.target.value)} className="w-20 text-center" data-testid={`ledger-item-qty-${i}`} /></td>
                          <td className="px-2 py-2"><Button size="sm" variant="ghost" onClick={() => removeEntryItem(i)} className="text-red-600"><Trash2 size={14} /></Button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-slate-500 mt-2">Total quantity: <span className="font-bold">{entryItems.reduce((s, i) => s + i.quantity, 0)}</span></p>
              </div>
            )}
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            {batchCount > 0 && (
              <div className="mr-auto text-xs text-slate-500 bg-slate-50 px-3 py-2 rounded-sm border" data-testid="batch-counter">
                Batch: <span className="font-bold text-slate-800">{batchCount}</span> entries saved, <span className="font-bold text-slate-800">{batchTotalQty}</span> total qty
              </div>
            )}
            <Button variant="outline" onClick={() => setCreateDialog(false)}>Cancel</Button>
            <Button variant="outline" onClick={() => handleSubmitEntry(true)} disabled={entryItems.length === 0 || submitting} className="border-slate-400 text-slate-700 hover:bg-slate-100" data-testid="save-and-add-more-btn">
              {submitting ? "Saving..." : "Save & Add More"}
            </Button>
            <Button onClick={() => handleSubmitEntry(false)} disabled={entryItems.length === 0 || submitting} className={entryType === "in" ? "bg-green-600 hover:bg-green-700 text-white" : "bg-red-600 hover:bg-red-700 text-white"} data-testid="submit-ledger-entry">
              {submitting ? "Saving..." : `Record Stock ${entryType === "in" ? "IN" : "OUT"}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Entry Dialog */}
      <Dialog open={viewDialog} onOpenChange={setViewDialog}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold uppercase flex items-center gap-2">
              {selectedEntry?.entry_type === "in" ? <ArrowDownCircle size={22} className="text-green-600" /> : <ArrowUpCircle size={22} className="text-red-600" />}
              Ledger Entry Details
              <span className={`text-xs px-2 py-1 rounded-sm font-bold uppercase ${selectedEntry?.entry_type === "in" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>{selectedEntry?.entry_type}</span>
            </DialogTitle>
          </DialogHeader>
          {selectedEntry && (
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div><p className="text-slate-500">Date</p><p className="font-medium">{new Date(selectedEntry.date).toLocaleDateString("en-IN")}</p></div>
                <div><p className="text-slate-500">PO Reference</p><p className="font-medium">{selectedEntry.po_reference || "N/A"}</p></div>
                <div><p className="text-slate-500">Transporter</p><p className="font-medium">{selectedEntry.transporter_name || "N/A"}</p></div>
                <div><p className="text-slate-500">Boxes</p><p className="font-medium">{selectedEntry.total_boxes || "N/A"}</p></div>
              </div>
              {selectedEntry.packaging_types?.length > 0 && (
                <div><p className="text-slate-500 text-sm">Packaging</p><div className="flex gap-2 mt-1">{selectedEntry.packaging_types.map((t, i) => <span key={`pkg-${t}-${i}`} className="px-2 py-1 bg-slate-100 text-slate-700 text-xs rounded-sm font-medium">{t}</span>)}</div></div>
              )}
              {selectedEntry.notes && <div><p className="text-slate-500 text-sm">Notes</p><p className="text-slate-700">{selectedEntry.notes}</p></div>}
              <div className="border rounded-sm overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100"><tr><th className="text-left px-4 py-3 text-xs font-bold uppercase">#</th><th className="text-left px-4 py-3 text-xs font-bold uppercase">Part #</th><th className="text-left px-4 py-3 text-xs font-bold uppercase">Name</th><th className="text-center px-4 py-3 text-xs font-bold uppercase">Qty</th></tr></thead>
                  <tbody className="divide-y">
                    {selectedEntry.items?.map((item, i) => (
                      <tr key={i} className="hover:bg-slate-50"><td className="px-4 py-3 text-slate-500">{i + 1}</td><td className="px-4 py-3 font-medium">{item.part_number}</td><td className="px-4 py-3">{item.name}</td><td className="px-4 py-3 text-center font-bold">{item.quantity}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-sm text-slate-500">Total: <span className="font-bold text-slate-900">{selectedEntry.total_quantity} units</span> | By: {selectedEntry.created_by_name} | {new Date(selectedEntry.created_at).toLocaleString("en-IN")}</p>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setViewDialog(false)}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StockLedger;
